import { useEffect, useState } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { Accordion, AccordionDetails, AccordionSummary, FormGroup } from "@mui/material";

//import redux store
import { saveHotelSelectedFilters } from "../../../store/actions";

//import customized hooks
import useDebouncedInputChange from "../../../hooks/useDebouncedInputChange ";

//import constants
import { IS_MOBILE } from "../../../constants/breakpoints";

//import general components
import BaseFilter from "../../common/base-filter/base-filter";
import FilterRangeSlider from "../../common/base-filter/filter-range-slider";
import BaseCheckbox from "../../common/base-checkbox/base-checkbox";
import BaseTextField from "../../common/base-text-field/base-text-field";
import { BaseButton } from "../../common/base-button/base-button";

//import images and icons
import { ArrowDown2 } from "iconsax-react";

function HotelFilter({ dispatch, HotelFilter, hotelUsedFilters, open, setOpen, setPage, setRequestFlag, type }) {
    //general variables
    const { t } = useTranslation();
    const { isHotelInternational } = JSON.parse(sessionStorage.getItem("generalInformation"));

    const [filterList, setFilterList] = useState(HotelFilter);
    const [usedFilters, setUsedFilters] = useState(hotelUsedFilters);
    useEffect(() => {
        setFilterList(HotelFilter);
        setUsedFilters(hotelUsedFilters);
    }, [isHotelInternational, HotelFilter, hotelUsedFilters]);

    //category names for filters
    const categoriesName = {
        name: `${t("search")} ${t("hotel")}`,
        prices: t("price"),
        stars: t("star"),
    };

    //define categories for range filters and option filters
    const searchFilterCategories = ["name"];
    const rangeFilterCategories = ["prices"];
    const optionFilterCategories = type === "hotel" ? ["stars"] : [];
    const allFilters = searchFilterCategories.concat(rangeFilterCategories, optionFilterCategories);

    //handle changes in filter selection
    const [selectedFilters, setSelectedFilters] = useState({});
    const activeFilterCount = Object.keys(selectedFilters).length || "";

    //search filter//
    const handleInputChange = event => {
        setPage(1);
        const { name, value } = event.target;
        const updatedFilters = {
            ...selectedFilters,
            [name]: value,
        };
        setSelectedFilters(updatedFilters);
    };

    //option filters//
    const handleOptionChange = (event, category) => {
        const { name, checked } = event.target;
        setPage(1);

        const updatedFilters = {
            ...selectedFilters,
            [category]: { ...selectedFilters?.[category], [name]: checked },
        };
        setSelectedFilters(updatedFilters);
    };

    //range slider filters//
    //define variables for range slider filters
    const [rangeValues, setRangeValues] = useState(
        rangeFilterCategories.reduce((map, obj) => {
            map[obj] = [0, filterList?.[obj]?.length - 1];
            return map;
        }, {}),
    );

    //handle change to the range slider for each filter
    const handleRangeChange = (event, newValue, filterName) => {
        setPage(1);
        setRangeValues(values => ({ ...values, [filterName]: newValue }));
    };

    //handle change to the range slider on mouse up and send the filtes to the parent component
    const handleRangeCommited = (event, _, filterName, from, to) => {
        const updatedFilters = { ...selectedFilters, [filterName]: [from, to] };
        setSelectedFilters(updatedFilters);
    };

    //this function handles the submission of filters on a mobile device.
    const handleSubmitFilter = () => {
        if (selectedFilters) {
            dispatch(saveHotelSelectedFilters(formatFiltersForSubmission(selectedFilters)));
            setRequestFlag(true);
        }
        setOpen(false);
        setPage(1);
    };

    // This function formats the selected filters for submission by converting the input object into a result object
    // where each category contains a comma-separated string of selected keys
    const formatFiltersForSubmission = input => {
        return Object.entries(input).reduce((result, [category, value]) => {
            if (optionFilterCategories.includes(category) && !!value) {
                const selectedKeys = Object?.keys(value)?.filter(key => value[key]);
                if (selectedKeys.length) {
                    result[category] = selectedKeys.join(",");
                }
            } else {
                result[category] = value;
            }
            return result;
        }, {});
    };

    //set default values for selected filters based on the provided defaultValue prop
    useEffect(() => {
        if (!usedFilters) return;

        const updatedFilters = Object.entries(usedFilters).reduce((acc, [filterName, filterValues]) => {
            if (searchFilterCategories.includes(filterName) && !!filterValues) {
                acc[filterName] = filterValues;
            }

            if (rangeFilterCategories.includes(filterName) && !!filterValues?.[0] && !!filterValues?.[1]) {
                setRangeValues(values => ({
                    ...values,
                    [filterName]: filterValues.map(value => filterList?.[filterName]?.findIndex(item => item === value)),
                }));
                acc[filterName] = filterValues;
            }

            if (optionFilterCategories.includes(filterName) && !!filterValues?.length) {
                acc[filterName] = filterValues?.reduce((map, airline) => ({ ...map, [airline]: true }), {});
            }

            return acc;
        }, {});

        setSelectedFilters(updatedFilters);
    }, [usedFilters, hotelUsedFilters]);

    //handle filter cancellation
    const resetFilters = () => {
        setPage(1);
        dispatch(saveHotelSelectedFilters(null));
        setRequestFlag(true);
        setOpen(false);
    };

    return (
        <>
            <BaseFilter
                open={open}
                setOpen={setOpen}
                handleSubmitFilter={handleSubmitFilter}
                resetFilters={resetFilters}
                activeFilterCount={activeFilterCount}
                top="0"
            >
                {filterList &&
                    allFilters?.map((filterKey, index) => (
                        <Accordion
                            key={index}
                            sx={{
                                width: "100%",
                                boxShadow: "0px 0px 3px 0px rgba(0, 0, 0, 0.25)",
                                margin: " 0 !important",
                            }}
                            defaultExpanded
                        >
                            <AccordionSummary
                                expandIcon={<ArrowDown2 color="var(--gray-color)" />}
                                sx={{
                                    minHeight: "fit-content !important",
                                    fontWeight: "700",
                                    "& .MuiAccordionSummary-content": { margin: "10px 0 !important" },
                                }}
                            >
                                {categoriesName?.[filterKey]}
                            </AccordionSummary>

                            <AccordionDetails sx={{ maxHeight: "20rem", overflowY: "scroll", "&::-webkit-scrollbar": { display: "none" } }}>
                                {searchFilterCategories.includes(filterKey) && (
                                    <BaseTextField
                                        label={t("hotelName")}
                                        name={filterKey}
                                        value={selectedFilters?.[filterKey]}
                                        setValue={e => handleInputChange(e)}
                                    />
                                )}
                                {rangeFilterCategories.includes(filterKey) && (
                                    <FilterRangeSlider
                                        filterList={filterList}
                                        filterName={filterKey}
                                        min={0}
                                        max={filterList?.[filterKey].length - 1}
                                        value={rangeValues[filterKey]}
                                        handleChange={handleRangeChange}
                                        handleChangeCommited={handleRangeCommited}
                                    />
                                )}
                                {optionFilterCategories.includes(filterKey) && !!filterList?.[filterKey]?.length && (
                                    <FormGroup className="base-filter__filter-label">
                                        {filterList?.[filterKey]
                                            ?.filter(filterItem => filterItem > 0)
                                            ?.map(filterItem => {
                                                return (
                                                    <BaseCheckbox
                                                        key={Math.random()}
                                                        label={`${filterItem} ${t("star")}`}
                                                        name={filterItem?.toString()}
                                                        checked={selectedFilters?.[filterKey]?.[filterItem]}
                                                        handleChange={handleOptionChange}
                                                        otherName={filterKey}
                                                    />
                                                );
                                            })}
                                    </FormGroup>
                                )}
                            </AccordionDetails>
                        </Accordion>
                    ))}
            </BaseFilter>
            {!IS_MOBILE && filterList && (
                <BaseButton text={t("General.Apply Filter")} color="primary" size="medium" radius="radius-10" handleClick={handleSubmitFilter} />
            )}
        </>
    );
}

export default connect(mapStateToProps)(HotelFilter);

function mapStateToProps(state) {
    return {
        HotelFilter: state.hotelData?.filters,
        hotelUsedFilters: state.hotelData?.used_filters,
    };
}
